import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { practitionerService } from '../../../services/practitionerService';
import { supabase } from '../../../lib/supabaseClient';

const PractitionerModal = ({ mode, practitioner, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    practiceName: '',
    practitionerTitle: 'Dr',
    practitionerName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    slug: '',
    primaryColor: '#2563eb',
    secondaryColor: '#1e40af',
    status: 'active',
    referralEmail: '',
    // New credential fields
    createUserAccount: true,
    userEmail: '',
    userPassword: '',
    autoGeneratePassword: true
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [generatedCredentials, setGeneratedCredentials] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(null);

  useEffect(() => {
    if (mode === 'edit' && practitioner) {
      setFormData({
        practiceName: practitioner?.practiceName || '',
        practitionerTitle: practitioner?.practitionerTitle || 'Dr',
        practitionerName: practitioner?.practitionerName || '',
        email: practitioner?.email || '',
        phone: practitioner?.phone || '',
        address: practitioner?.address || '',
        city: practitioner?.city || '',
        postalCode: practitioner?.postalCode || '',
        slug: practitioner?.slug || '',
        primaryColor: practitioner?.primaryColor || '#2563eb',
        secondaryColor: practitioner?.secondaryColor || '#1e40af',
        status: practitioner?.status || 'active',
        referralEmail: practitioner?.referralEmail || '',
        createUserAccount: false,
        userEmail: '',
        userPassword: '',
        autoGeneratePassword: true
      });
    }
  }, [mode, practitioner]);

  const generateSlug = (name) => {
    return name
      ?.toLowerCase()
      ?.normalize('NFD')
      ?.replace(/[\u0300-\u036f]/g, '')
      ?.replace(/[^a-z0-9]+/g, '-')
      ?.replace(/^-+|-+$/g, '');
  };

  const generateRandomPassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset?.charAt(Math.floor(Math.random() * charset?.length));
    }
    return password;
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const checkSlugAvailability = async (slug) => {
    if (!slug || mode === 'edit') return;
    
    setCheckingSlug(true);
    try {
      const { data, error } = await supabase?.from('practitioners')?.select('id')?.eq('slug', slug)?.single();

      if (error && error?.code === 'PGRST116') {
        // No rows returned - slug is available
        setSlugAvailable(true);
        setValidationErrors(prev => ({ ...prev, slug: null }));
      } else if (data) {
        // Slug already exists
        setSlugAvailable(false);
        setValidationErrors(prev => ({ ...prev, slug: 'Ce slug est déjà utilisé' }));
      }
    } catch (err) {
      console.error('Error checking slug:', err);
    } finally {
      setCheckingSlug(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    const newValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
    
    // Clear validation error for this field
    setValidationErrors(prev => ({ ...prev, [name]: null }));
    
    // Auto-generate slug from practice name
    if (name === 'practiceName' && mode === 'create') {
      const generatedSlug = generateSlug(value);
      setFormData(prev => ({ ...prev, slug: generatedSlug }));
      // Check slug availability after a delay
      setTimeout(() => checkSlugAvailability(generatedSlug), 500);
    }

    // Validate email
    if (name === 'email' && value) {
      if (!validateEmail(value)) {
        setValidationErrors(prev => ({ ...prev, email: 'Format d\'email invalide' }));
      }
    }

    // Validate user email
    if (name === 'userEmail' && value) {
      if (!validateEmail(value)) {
        setValidationErrors(prev => ({ ...prev, userEmail: 'Format d\'email invalide' }));
      }
    }

    // Check slug availability when manually changed
    if (name === 'slug' && mode === 'create') {
      setSlugAvailable(null);
      setTimeout(() => checkSlugAvailability(value), 500);
    }
    
    if (error) setError('');
  };

  useEffect(() => {
    if (formData?.autoGeneratePassword && mode === 'create' && formData?.createUserAccount) {
      setFormData(prev => ({ ...prev, userPassword: generateRandomPassword() }));
    }
  }, [formData?.autoGeneratePassword, formData?.createUserAccount, mode]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Validation
    const errors = {};
    
    if (!formData?.practiceName) errors.practiceName = 'Nom du cabinet requis';
    if (!formData?.practitionerName) errors.practitionerName = 'Nom du praticien requis';
    if (!formData?.email) {
      errors.email = 'Email requis';
    } else if (!validateEmail(formData?.email)) {
      errors.email = 'Format d\'email invalide';
    }
    if (!formData?.slug) errors.slug = 'Slug requis';
    if (mode === 'create' && slugAvailable === false) {
      errors.slug = 'Ce slug est déjà utilisé';
    }

    if (mode === 'create' && formData?.createUserAccount) {
      if (!formData?.userEmail) {
        errors.userEmail = 'Email utilisateur requis';
      } else if (!validateEmail(formData?.userEmail)) {
        errors.userEmail = 'Format d\'email invalide';
      }
      if (!formData?.userPassword) errors.userPassword = 'Mot de passe requis';
    }

    if (Object.keys(errors)?.length > 0) {
      setValidationErrors(errors);
      setError('Veuillez corriger les erreurs dans le formulaire');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      let practitionerResult;
      
      if (mode === 'create') {
        practitionerResult = await practitionerService?.create(formData);

        // Create user account if requested
        if (formData?.createUserAccount && practitionerResult?.id) {
          const fullName = `${formData?.practitionerTitle} ${formData?.practitionerName}`;
          const userResult = await practitionerService?.createUserAccount(
            formData?.userEmail,
            formData?.userPassword,
            fullName,
            practitionerResult?.id
          );

          if (userResult?.user) {
            setGeneratedCredentials({
              email: formData?.userEmail,
              password: formData?.userPassword,
              practitionerName: fullName,
              dashboardUrl: `${window.location?.origin}/individual-practitioner-dashboard`,
              publicUrl: practitionerService?.getPublicUrl(practitionerResult?.slug)
            });
          }
        }
      } else {
        await practitionerService?.update(practitioner?.id, formData);
      }
      
      // If credentials were generated, show them before closing
      if (!generatedCredentials) {
        onSave?.();
      }
    } catch (err) {
      console.error('Error saving practitioner:', err);
      setError(err?.message || "Erreur lors de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseWithCredentials = () => {
    setGeneratedCredentials(null);
    onSave?.();
  };

  const copyToClipboard = (text) => {
    navigator?.clipboard?.writeText(text);
  };

  // Show credentials modal if they were generated
  if (generatedCredentials) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-card rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white/8">
          <div className="sticky top-0 bg-card-elevated/95 backdrop-blur-xl border-b border-white/8 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 bg-white/20 rounded-xl">
                <Icon name="CheckCircle" size={28} className="text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Praticien créé avec succès!</h2>
                <p className="text-sm text-white/90">Identifiants de connexion générés</p>
              </div>
            </div>
            <Button
              onClick={onClose}
              className="p-2 text-muted hover:text-primary hover:bg-card-hover rounded-lg transition-colors"
            >
              <Icon name="X" size={24} />
            </Button>
          </div>

          <div className="p-6 space-y-6">
            <div className="bg-warning-bg border border-warning/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-primary mb-1">Attention - Informations importantes</p>
                  <p className="text-sm text-secondary">
                    Ces identifiants ne seront plus affichés. Copiez-les maintenant et transmettez-les au praticien de manière sécurisée.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-card-elevated rounded-lg p-4 space-y-3 border border-white/8">
                <div>
                  <label className="text-xs font-medium text-muted uppercase tracking-wide">Praticien</label>
                  <p className="text-lg font-semibold text-primary">{generatedCredentials?.practitionerName}</p>
                </div>

                <div className="border-t border-white/8 pt-3">
                  <label className="text-xs font-medium text-muted uppercase tracking-wide">Email de connexion</label>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-sm font-mono text-primary flex-1">{generatedCredentials?.email}</p>
                    <button
                      onClick={() => copyToClipboard(generatedCredentials?.email)}
                      className="p-2 text-muted hover:text-accent hover:bg-card-hover rounded-lg transition-colors"
                      title="Copier"
                    >
                      <Icon name="Copy" size={16} />
                    </button>
                  </div>
                </div>

                <div className="border-t border-white/8 pt-3">
                  <label className="text-xs font-medium text-muted uppercase tracking-wide">Mot de passe</label>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-sm font-mono text-primary flex-1 bg-card px-3 py-2 rounded border border-white/8">
                      {generatedCredentials?.password}
                    </p>
                    <button
                      onClick={() => copyToClipboard(generatedCredentials?.password)}
                      className="p-2 text-muted hover:text-accent hover:bg-card-hover rounded-lg transition-colors"
                      title="Copier"
                    >
                      <Icon name="Copy" size={16} />
                    </button>
                  </div>
                </div>

                <div className="border-t border-white/8 pt-3">
                  <label className="text-xs font-medium text-muted uppercase tracking-wide">URL du tableau de bord</label>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-sm text-accent flex-1 truncate">{generatedCredentials?.dashboardUrl}</p>
                    <button
                      onClick={() => copyToClipboard(generatedCredentials?.dashboardUrl)}
                      className="p-2 text-muted hover:text-accent hover:bg-card-hover rounded-lg transition-colors"
                      title="Copier"
                    >
                      <Icon name="Copy" size={16} />
                    </button>
                  </div>
                </div>

                <div className="border-t border-white/8 pt-3">
                  <label className="text-xs font-medium text-muted uppercase tracking-wide">URL publique d'adressage</label>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-sm text-accent flex-1 truncate">{generatedCredentials?.publicUrl}</p>
                    <button
                      onClick={() => copyToClipboard(generatedCredentials?.publicUrl)}
                      className="p-2 text-muted hover:text-accent hover:bg-card-hover rounded-lg transition-colors"
                      title="Copier"
                    >
                      <Icon name="Copy" size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleCloseWithCredentials}
                className="flex-1 px-6 py-3 bg-accent hover:bg-accent/90 text-white rounded-lg font-medium transition-colors"
              >
                Terminé
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-white/8">
        {/* Header */}
        <div className="sticky top-0 bg-card-elevated/95 backdrop-blur-xl border-b border-white/8 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-accent/20 rounded-xl">
              <Icon name="UserPlus" size={24} className="text-accent" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-primary">
                {mode === 'create' ? 'Nouveau Praticien' : 'Modifier le Praticien'}
              </h2>
              <p className="text-sm text-muted">Informations du cabinet et du praticien</p>
            </div>
          </div>
          <Button
            onClick={onClose}
            className="p-2 text-muted hover:text-primary hover:bg-card-hover rounded-lg transition-colors"
          >
            <Icon name="X" size={24} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-error-bg border border-error/30 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-sm text-secondary">{error}</p>
            </div>
          )}

          {/* Practice Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
              <Icon name="Building2" size={20} className="text-accent" />
              Informations du Cabinet
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary mb-2">
                  Nom du Cabinet <span className="text-error">*</span>
                </label>
                <Input
                  name="practiceName"
                  value={formData?.practiceName}
                  onChange={handleInputChange}
                  placeholder="Cabinet Dr Dupont - Endodontie"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-secondary mb-2">
                  Titre
                </label>
                <select
                  name="practitionerTitle"
                  value={formData?.practitionerTitle}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                >
                  <option value="Dr">Dr</option>
                  <option value="Pr">Pr</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-secondary mb-2">
                  Nom du Praticien <span className="text-error">*</span>
                </label>
                <Input
                  name="practitionerName"
                  value={formData?.practitionerName}
                  onChange={handleInputChange}
                  placeholder="Dupont"
                  required
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
              <Icon name="Mail" size={20} className="text-accent" />
              Coordonnées
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary mb-2">
                  Email <span className="text-error">*</span>
                </label>
                <Input
                  name="email"
                  type="email"
                  value={formData?.email}
                  onChange={handleInputChange}
                  placeholder="contact@cabinet.fr"
                  required
                  error={validationErrors?.email}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-secondary mb-2">
                  Téléphone
                </label>
                <Input
                  name="phone"
                  type="tel"
                  value={formData?.phone}
                  onChange={handleInputChange}
                  placeholder="+33 1 23 45 67 89"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary mb-2">
                  Email de Réception des Référals
                </label>
                <Input
                  name="referralEmail"
                  type="email"
                  value={formData?.referralEmail}
                  onChange={handleInputChange}
                  placeholder="referrals@cabinet.fr (optionnel)"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary mb-2">
                  Adresse
                </label>
                <Input
                  name="address"
                  value={formData?.address}
                  onChange={handleInputChange}
                  placeholder="15 rue de la République"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-secondary mb-2">
                  Ville
                </label>
                <Input
                  name="city"
                  value={formData?.city}
                  onChange={handleInputChange}
                  placeholder="Paris"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-secondary mb-2">
                  Code Postal
                </label>
                <Input
                  name="postalCode"
                  value={formData?.postalCode}
                  onChange={handleInputChange}
                  placeholder="75001"
                />
              </div>
            </div>
          </div>

          {/* URL Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
              <Icon name="Link" size={20} className="text-accent" />
              Configuration URL
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">
                URL Slug <span className="text-error">*</span>
              </label>
              <Input
                name="slug"
                value={formData?.slug}
                onChange={handleInputChange}
                placeholder="dr-dupont-endodontie"
                required
                disabled={mode === 'edit'}
                error={validationErrors?.slug}
              />
              {mode === 'create' && checkingSlug && (
                <p className="text-xs text-muted mt-1 flex items-center gap-1">
                  <div className="animate-spin rounded-full h-3 w-3 border-b border-accent"></div>
                  Vérification de la disponibilité...
                </p>
              )}
              {mode === 'create' && slugAvailable === true && (
                <p className="text-xs text-success mt-1 flex items-center gap-1">
                  <Icon name="CheckCircle" size={12} />
                  Slug disponible
                </p>
              )}
              {mode === 'create' && slugAvailable === false && (
                <p className="text-xs text-error mt-1 flex items-center gap-1">
                  <Icon name="XCircle" size={12} />
                  Slug déjà utilisé
                </p>
              )}
              <p className="text-xs text-muted mt-1">
                URL publique: {window.location?.origin}/p/{formData?.slug || 'slug'}
              </p>
            </div>
          </div>

          {/* Status */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
              <Icon name="Settings" size={20} className="text-accent" />
              Statut
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">
                Statut du Compte
              </label>
              <select
                name="status"
                value={formData?.status}
                onChange={handleInputChange}
                className="w-full px-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
              >
                <option value="active">Actif</option>
                <option value="pending">En attente</option>
                <option value="inactive">Inactif</option>
              </select>
            </div>
          </div>

          {/* User Account Creation (Create Mode Only) */}
          {mode === 'create' && (
            <div className="space-y-4 border-t border-white/8 pt-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
                  <Icon name="Key" size={20} className="text-accent" />
                  Compte Utilisateur Endodontiste
                </h3>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="createUserAccount"
                    checked={formData?.createUserAccount}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-accent border-white/8 rounded focus:ring-accent"
                  />
                  <span className="text-sm font-medium text-secondary">Créer un compte d'accès</span>
                </label>
              </div>

              {formData?.createUserAccount && (
                <div className="space-y-4 bg-accent/10 border border-accent/30 rounded-lg p-4">
                  <p className="text-sm text-secondary">
                    Un compte utilisateur sera créé pour permettre au praticien d'accéder à son tableau de bord.
                  </p>

                  <div>
                    <label className="block text-sm font-medium text-secondary mb-2">
                      Email de connexion <span className="text-error">*</span>
                    </label>
                    <Input
                      name="userEmail"
                      type="email"
                      value={formData?.userEmail}
                      onChange={handleInputChange}
                      placeholder="praticien@practitioner.endoreferral.fr"
                      required={formData?.createUserAccount}
                    />
                    <p className="text-xs text-muted mt-1">
                      Format suggéré: {formData?.slug}@practitioner.endoreferral.fr
                    </p>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 mb-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="autoGeneratePassword"
                        checked={formData?.autoGeneratePassword}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-accent border-white/8 rounded focus:ring-accent"
                      />
                      <span className="text-sm font-medium text-secondary">Générer un mot de passe automatiquement</span>
                    </label>

                    <div className="relative">
                      <Input
                        name="userPassword"
                        type={showPassword ? 'text' : 'password'}
                        value={formData?.userPassword}
                        onChange={handleInputChange}
                        placeholder="Mot de passe"
                        required={formData?.createUserAccount}
                        disabled={formData?.autoGeneratePassword}
                        className={formData?.autoGeneratePassword ? 'opacity-50' : ''}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
                      </button>
                    </div>
                    {formData?.autoGeneratePassword && (
                      <p className="text-xs text-muted mt-1">
                        Un mot de passe sécurisé a été généré automatiquement
                      </p>
                    )}
                  </div>

                  <div className="bg-warning-bg border border-warning/30 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <Icon name="AlertTriangle" size={16} className="text-warning flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-secondary">
                        <strong className="text-primary">Important:</strong> Notez ces identifiants. Ils seront affichés après la création et ne pourront plus être récupérés.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/8">
            <Button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-secondary hover:bg-card-hover rounded-lg font-medium transition-colors"
              disabled={loading}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              className="px-6 py-2 bg-accent hover:bg-accent/90 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Enregistrement...</span>
                </>
              ) : (
                <>
                  <Icon name="Save" size={18} />
                  <span>{mode === 'create' ? 'Créer' : 'Enregistrer'}</span>
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PractitionerModal;